/**
 * FieldRulesEditor — visual builder for Advanced Field Rules.
 *
 * Controlled component. Edits the typed `rules` array (an array of FieldRule
 * objects — see `src/types/formSchema.js` and `src/utils/ruleEvaluator.js`).
 *
 * A rule lives on the field it acts on (the target / current field) and may
 * reference sibling fields by their stable `id`/`key`.
 *
 * Effects:
 *   - visibility : Show / Hide this field when the `when` conditions match.
 *   - cascade    : Filter this field's options to those whose option `group`
 *                  equals the current value of the `controlledBy` field.
 *   - format     : Colour this field (and its displayed value) when `when`
 *                  matches. A `flag` badge is always paired with colour so
 *                  styling is never conveyed by colour alone (WCAG AA).
 */
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { X, Plus, Eye, EyeOff, Filter, Palette, Info, Code2 } from 'lucide-react';
import { StyledSelect } from '../../FormStyles';
import {
  Stack,
  RuleCard,
  RuleIndex,
  FieldLabel,
  TextInput,
  SelectWrap,
  DeleteBtn,
  AddButton,
  Empty,
} from './ruleEditorStyles';

// ── Operator vocabulary with friendly labels ────────────────────────────────
// Mirrors RULE_OPERATORS in ruleEvaluator.js.

const OPERATORS = [
  { value: 'equals', label: 'is equal to', kind: 'value' },
  { value: 'notEquals', label: 'is not equal to', kind: 'value' },
  { value: 'gt', label: 'is greater than', kind: 'value' },
  { value: 'gte', label: 'is greater than or equal to', kind: 'value' },
  { value: 'lt', label: 'is less than', kind: 'value' },
  { value: 'lte', label: 'is less than or equal to', kind: 'value' },
  { value: 'between', label: 'is between', kind: 'range' },
  { value: 'contains', label: 'contains', kind: 'value' },
  { value: 'startsWith', label: 'starts with', kind: 'value' },
  { value: 'in', label: 'is one of', kind: 'list' },
  { value: 'isEmpty', label: 'is empty', kind: 'none' },
  { value: 'isNotEmpty', label: 'is not empty', kind: 'none' },
  { value: 'before', label: 'is before', kind: 'value' },
  { value: 'after', label: 'is after', kind: 'value' },
];

const EFFECTS = [
  { value: 'show', label: 'Show this field', icon: Eye },
  { value: 'hide', label: 'Hide this field', icon: EyeOff },
  { value: 'cascade', label: 'Filter options', icon: Filter },
  { value: 'format', label: 'Colour this field', icon: Palette },
];

const operatorKind = (op) => (OPERATORS.find(o => o.value === op) || {}).kind || 'value';

// Field types whose values can be offered as a dropdown of options.
const CHOICE_TYPES = new Set(['select', 'radio', 'checkbox_group']);

// Field types that own a list of options and can therefore be cascade targets.
const CASCADE_TARGET_TYPES = new Set(['select', 'radio', 'checkbox_group', 'tags']);

let RULE_SEQ = 0;
/** Generate a reasonably unique rule id. */
const newRuleId = () => `rule_${Date.now().toString(36)}_${(RULE_SEQ++).toString(36)}`;

// ── Panel-local styled primitives ───────────────────────────────────────────

const EffectGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing.xs};
`;

const EffectButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  padding: ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.md};
  border: 1px solid ${props => props.$active ? props.theme.colors.tint : props.theme.colors.border};
  background: ${props => props.$active ? `${props.theme.colors.tint}15` : props.theme.colors.backgroundSecondary};
  color: ${props => props.$active ? props.theme.colors.tint : props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.small};
  font-weight: ${props => props.$active ? props.theme.typography.weights.semiBold : props.theme.typography.weights.normal};
  cursor: pointer;
  text-align: left;
  transition: all 0.15s ease;

  &:hover {
    border-color: ${props => props.theme.colors.tint};
  }
`;

const ConditionRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr auto;
  gap: ${props => props.theme.spacing.xs};
  align-items: center;
`;

const LogicRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  font-size: ${props => props.theme.typography.sizes.small};
  color: ${props => props.theme.colors.muted};
`;

const SmallSelectWrap = styled(SelectWrap)`
  select { min-height: 36px; }
`;

const ConditionDelete = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 36px;
  border: 1px solid transparent;
  border-radius: ${props => props.theme.borderRadius.md};
  background: transparent;
  color: ${props => props.theme.colors.muted};
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.15s ease;

  &:hover {
    color: ${props => props.theme.colors.danger};
    background: ${props => props.theme.colors.danger}15;
    border-color: ${props => props.theme.colors.danger}40;
  }
`;

const Hint = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${props => props.theme.spacing.xs};
  padding: ${props => props.theme.spacing.sm};
  border: 1px dashed ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background: ${props => props.theme.colors.backgroundSecondary};
  color: ${props => props.theme.colors.muted};
  font-size: ${props => props.theme.typography.sizes.small};
  line-height: 1.45;

  svg { flex-shrink: 0; margin-top: 1px; }
`;

const StyleGrid = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: ${props => props.theme.spacing.sm};
  align-items: center;
`;

const ColorSwatch = styled.input.attrs({ type: 'color' })`
  width: 40px;
  height: 36px;
  padding: 2px;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background: ${props => props.theme.colors.backgroundSecondary};
  cursor: pointer;
`;

const FlagPreview = styled.span`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  padding: 2px 8px;
  border-radius: ${props => props.theme.borderRadius.lg};
  font-size: ${props => props.theme.typography.sizes.small};
  font-weight: ${props => props.theme.typography.weights.semiBold};
  background: ${props => props.$bg || props.theme.colors.tint};
  color: ${props => props.$fg || '#ffffff'};
  border: 1px solid ${props => props.theme.colors.border};
`;

const JsonToggle = styled.button`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  align-self: flex-end;
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background: ${props => props.$active ? `${props.theme.colors.tint}15` : 'transparent'};
  color: ${props => props.$active ? props.theme.colors.tint : props.theme.colors.muted};
  font-size: ${props => props.theme.typography.sizes.small};
  font-weight: ${props => props.theme.typography.weights.semiBold};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover { border-color: ${props => props.theme.colors.tint}; }
`;

const JsonArea = styled.textarea`
  width: 100%;
  min-height: 180px;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.$invalid ? props.theme.colors.danger : props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background: ${props => props.theme.colors.backgroundSecondary};
  color: ${props => props.theme.colors.text};
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: ${props => props.theme.typography.sizes.small};
  line-height: 1.5;
  box-sizing: border-box;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${props => props.$invalid ? props.theme.colors.danger : props.theme.colors.tint};
    box-shadow: 0 0 0 2px ${props => (props.$invalid ? props.theme.colors.danger : props.theme.colors.tint)}20;
  }
`;

const JsonError = styled.div`
  color: ${props => props.theme.colors.danger};
  font-size: ${props => props.theme.typography.sizes.small};
`;

// ── Rule shape helpers ──────────────────────────────────────────────────────

/** Map a stored FieldRule to the editor's effect-key (show/hide/cascade/format). */
const ruleEffectKey = (rule) => {
  if (rule.effect === 'cascade') return 'cascade';
  if (rule.effect === 'format') return 'format';
  return rule.action === 'hide' ? 'hide' : 'show';
};

/** Build a fresh rule for the given editor effect-key. */
const makeRule = (effectKey) => {
  const base = { id: newRuleId() };
  if (effectKey === 'cascade') {
    return { ...base, effect: 'cascade', controlledBy: '' };
  }
  if (effectKey === 'format') {
    return {
      ...base,
      effect: 'format',
      when: { logic: 'and', conditions: [emptyCondition()] },
      style: { bg: '#fee2e2', fg: '#991b1b', flag: '' },
    };
  }
  return {
    ...base,
    effect: 'visibility',
    action: effectKey === 'hide' ? 'hide' : 'show',
    when: { logic: 'and', conditions: [emptyCondition()] },
  };
};

const emptyCondition = () => ({ field: '', op: 'equals', value: '' });

// ── Condition editor ────────────────────────────────────────────────────────

const ConditionEditor = ({ rule, ruleIndex, siblingFields, onChange }) => {
  const group = rule.when || { logic: 'and', conditions: [] };
  const conditions = Array.isArray(group.conditions) ? group.conditions : [];

  const commit = (next) => onChange({ ...rule, when: next });

  const setLogic = (logic) => commit({ ...group, logic });

  const updateCondition = (idx, patch) => {
    const next = conditions.map((c, i) => (i === idx ? { ...c, ...patch } : c));
    commit({ ...group, conditions: next });
  };

  const changeOperator = (idx, op) => {
    const cond = { ...conditions[idx], op };
    const kind = operatorKind(op);
    // Keep only the value keys the operator needs.
    delete cond.value;
    delete cond.valueTo;
    delete cond.values;
    if (kind === 'value') cond.value = '';
    else if (kind === 'range') { cond.value = ''; cond.valueTo = ''; }
    else if (kind === 'list') cond.values = [];
    updateConditionFull(idx, cond);
  };

  const updateConditionFull = (idx, cond) => {
    const next = conditions.map((c, i) => (i === idx ? cond : c));
    commit({ ...group, conditions: next });
  };

  const removeCondition = (idx) => {
    commit({ ...group, conditions: conditions.filter((_, i) => i !== idx) });
  };

  const addCondition = () => {
    commit({ ...group, conditions: [...conditions, emptyCondition()] });
  };

  return (
    <div>
      <FieldLabel>When</FieldLabel>
      <Stack>
        {conditions.length === 0 && (
          <Empty>No conditions — this rule always applies. Add a condition below.</Empty>
        )}
        {conditions.map((cond, idx) => {
          const kind = operatorKind(cond.op);
          const triggerField = siblingFields.find(f => f.id === cond.field);
          const triggerOptions = triggerField && CHOICE_TYPES.has(triggerField.type)
            ? (triggerField.props?.options || [])
            : null;
          return (
            <div key={idx}>
              {idx > 0 && (
                <LogicRow>
                  <span>{group.logic === 'or' ? 'or' : 'and'}</span>
                </LogicRow>
              )}
              <ConditionRow>
                {/* Trigger field */}
                <SmallSelectWrap>
                  <StyledSelect
                    aria-label={`Trigger field for condition ${idx + 1}`}
                    value={cond.field || ''}
                    onChange={(e) => updateCondition(idx, { field: e.target.value })}
                  >
                    <option value="">Select field…</option>
                    {siblingFields.map(f => (
                      <option key={f.id} value={f.id}>{f.label || f.id}</option>
                    ))}
                  </StyledSelect>
                </SmallSelectWrap>

                {/* Operator */}
                <SmallSelectWrap>
                  <StyledSelect
                    aria-label={`Operator for condition ${idx + 1}`}
                    value={cond.op || 'equals'}
                    onChange={(e) => changeOperator(idx, e.target.value)}
                  >
                    {OPERATORS.map(op => (
                      <option key={op.value} value={op.value}>{op.label}</option>
                    ))}
                  </StyledSelect>
                </SmallSelectWrap>

                {/* Value */}
                {kind === 'none' && <span />}
                {kind === 'value' && (
                  triggerOptions ? (
                    <SmallSelectWrap>
                      <StyledSelect
                        aria-label={`Value for condition ${idx + 1}`}
                        value={cond.value ?? ''}
                        onChange={(e) => updateCondition(idx, { value: e.target.value })}
                      >
                        <option value="">Select value…</option>
                        {triggerOptions.map((o, i) => (
                          <option key={i} value={o.value}>{o.label || o.value}</option>
                        ))}
                      </StyledSelect>
                    </SmallSelectWrap>
                  ) : (
                    <TextInput
                      aria-label={`Value for condition ${idx + 1}`}
                      value={cond.value ?? ''}
                      onChange={(e) => updateCondition(idx, { value: e.target.value })}
                      placeholder="Value"
                    />
                  )
                )}
                {kind === 'range' && (
                  <div style={{ display: 'flex', gap: 4 }}>
                    <TextInput
                      aria-label={`Minimum for condition ${idx + 1}`}
                      value={cond.value ?? ''}
                      onChange={(e) => updateCondition(idx, { value: e.target.value })}
                      placeholder="Min"
                    />
                    <TextInput
                      aria-label={`Maximum for condition ${idx + 1}`}
                      value={cond.valueTo ?? ''}
                      onChange={(e) => updateCondition(idx, { valueTo: e.target.value })}
                      placeholder="Max"
                    />
                  </div>
                )}
                {kind === 'list' && (
                  <TextInput
                    aria-label={`Values for condition ${idx + 1}`}
                    value={Array.isArray(cond.values) ? cond.values.join(', ') : ''}
                    onChange={(e) => updateCondition(idx, {
                      values: e.target.value.split(',').map(v => v.trim()).filter(Boolean),
                    })}
                    placeholder="Comma-separated values"
                  />
                )}

                <ConditionDelete
                  type="button"
                  aria-label={`Remove condition ${idx + 1}`}
                  title="Remove condition"
                  onClick={() => removeCondition(idx)}
                >
                  <X size={15} />
                </ConditionDelete>
              </ConditionRow>
            </div>
          );
        })}

        {conditions.length > 1 && (
          <LogicRow>
            <span>Match</span>
            <SmallSelectWrap style={{ width: 120 }}>
              <StyledSelect
                aria-label={`Match logic for rule ${ruleIndex + 1}`}
                value={group.logic === 'or' ? 'or' : 'and'}
                onChange={(e) => setLogic(e.target.value)}
              >
                <option value="and">all conditions</option>
                <option value="or">any condition</option>
              </StyledSelect>
            </SmallSelectWrap>
          </LogicRow>
        )}

        <AddButton type="button" onClick={addCondition}>
          <Plus size={14} />
          Add Condition
        </AddButton>
      </Stack>
    </div>
  );
};

// ── Single rule card ────────────────────────────────────────────────────────

const RuleEditorCard = ({ rule, index, siblingFields, cascadeFields, formatSiblingFields, availableEffects, onChange, onRemove }) => {
  const effectKey = ruleEffectKey(rule);

  const setEffect = (nextKey) => {
    if (nextKey === effectKey) return;
    // Replace the rule with a fresh one of the chosen effect, preserving id.
    onChange({ ...makeRule(nextKey), id: rule.id });
  };

  const style = rule.style || {};
  const updateStyle = (patch) => onChange({ ...rule, style: { ...style, ...patch } });

  return (
    <RuleCard>
      <RuleIndex>Rule {index + 1}</RuleIndex>

      <div>
        <FieldLabel>Effect</FieldLabel>
        <EffectGrid>
          {availableEffects.map(({ value, label, icon: Icon }) => (
            <EffectButton
              key={value}
              type="button"
              $active={effectKey === value}
              aria-pressed={effectKey === value}
              onClick={() => setEffect(value)}
            >
              <Icon size={14} />
              {label}
            </EffectButton>
          ))}
        </EffectGrid>
      </div>

      {/* visibility & format share the When editor */}
      {(effectKey === 'show' || effectKey === 'hide' || effectKey === 'format') && (
        <ConditionEditor
          rule={rule}
          ruleIndex={index}
          siblingFields={effectKey === 'format' ? formatSiblingFields : siblingFields}
          onChange={onChange}
        />
      )}

      {/* cascade — controlling field picker */}
      {effectKey === 'cascade' && (
        <div>
          <FieldLabel>Controlled by</FieldLabel>
          <SelectWrap>
            <StyledSelect
              aria-label={`Controlling field for rule ${index + 1}`}
              value={rule.controlledBy || ''}
              onChange={(e) => onChange({ ...rule, controlledBy: e.target.value })}
            >
              <option value="">Select controlling field…</option>
              {cascadeFields.map(f => (
                <option key={f.id} value={f.id}>{f.label || f.id}</option>
              ))}
            </StyledSelect>
          </SelectWrap>
          <Hint style={{ marginTop: 8 }}>
            <Info size={14} />
            <span>
              This field's options are filtered to those whose <strong>Group</strong> matches
              the controlling field's value. Tag each option in the Options editor's
              Group column.
            </span>
          </Hint>
        </div>
      )}

      {/* format — style editor */}
      {effectKey === 'format' && (
        <div>
          <FieldLabel>Colour &amp; badge</FieldLabel>
          <Stack>
            <StyleGrid>
              <ColorSwatch
                aria-label="Background colour"
                title="Background colour"
                value={style.bg || '#fee2e2'}
                onChange={(e) => updateStyle({ bg: e.target.value })}
              />
              <span style={{ fontSize: 14 }}>Background colour</span>
              <ColorSwatch
                aria-label="Text colour"
                title="Text colour"
                value={style.fg || '#991b1b'}
                onChange={(e) => updateStyle({ fg: e.target.value })}
              />
              <span style={{ fontSize: 14 }}>Text colour</span>
            </StyleGrid>
            <TextInput
              aria-label="Badge text"
              value={style.flag || ''}
              onChange={(e) => updateStyle({ flag: e.target.value })}
              placeholder="Badge text (e.g. Out of range)"
            />
            <Hint>
              <Info size={14} />
              <span>
                Always set a short badge text — colour alone is not accessible. The badge
                shows beside the field and its submitted value.
              </span>
            </Hint>
            {style.flag && (
              <FlagPreview $bg={style.bg} $fg={style.fg}>{style.flag}</FlagPreview>
            )}
          </Stack>
        </div>
      )}

      <DeleteBtn
        type="button"
        aria-label={`Remove rule ${index + 1}`}
        title="Remove rule"
        onClick={onRemove}
      >
        <X size={16} />
      </DeleteBtn>
    </RuleCard>
  );
};

// ── Main component ──────────────────────────────────────────────────────────

/**
 * @param {Object} props
 * @param {import('../../../types/formSchema').FieldRule[]} props.rules - Current rules.
 * @param {Array<{id:string,label:string,type:string,props:Object}>} props.siblingFields
 *        - Candidate trigger/controlling fields (current + layout fields excluded by caller).
 * @param {Object} props.currentField - The field these rules act on.
 * @param {(nextRules:Array)=>void} props.onChange - Controlled change handler.
 */
export const FieldRulesEditor = ({ rules = [], siblingFields = [], currentField, onChange }) => {
  const [jsonMode, setJsonMode] = useState(false);
  const [jsonDraft, setJsonDraft] = useState('');
  const [jsonError, setJsonError] = useState('');

  // Cascade controlling fields = choice fields among siblings (a sensible subset).
  const cascadeFields = useMemo(
    () => siblingFields.filter(f => CHOICE_TYPES.has(f.type)),
    [siblingFields]
  );

  // Colour (format) rules may legitimately test the field's OWN value (e.g. turn
  // a reading red when it exceeds a threshold), so the current field is a valid
  // trigger there. Cascade/visibility keep self excluded (handled by the caller).
  const formatSiblingFields = useMemo(() => {
    if (!currentField?.id) return siblingFields;
    const selfEntry = {
      id: currentField.id,
      label: currentField.props?.label || currentField.id,
      type: currentField.type,
      props: currentField.props,
    };
    return [selfEntry, ...siblingFields];
  }, [currentField, siblingFields]);

  // "Filter options" (cascade) only makes sense on a field that owns options —
  // don't offer it on text, number, date, etc.
  const availableEffects = useMemo(
    () => (CASCADE_TARGET_TYPES.has(currentField?.type)
      ? EFFECTS
      : EFFECTS.filter(e => e.value !== 'cascade')),
    [currentField?.type]
  );

  const updateRule = (index, nextRule) => {
    onChange(rules.map((r, i) => (i === index ? nextRule : r)));
  };

  const removeRule = (index) => {
    onChange(rules.filter((_, i) => i !== index));
  };

  const addRule = () => {
    onChange([...rules, makeRule('show')]);
  };

  const enterJsonMode = () => {
    setJsonDraft(JSON.stringify(rules, null, 2));
    setJsonError('');
    setJsonMode(true);
  };

  const handleJsonChange = (text) => {
    setJsonDraft(text);
    try {
      const parsed = JSON.parse(text);
      if (!Array.isArray(parsed)) {
        setJsonError('Rules must be a JSON array.');
        return;
      }
      setJsonError('');
      onChange(parsed);
    } catch (e) {
      setJsonError(`Invalid JSON: ${e.message}`);
    }
  };

  return (
    <Stack>
      <JsonToggle
        type="button"
        $active={jsonMode}
        aria-pressed={jsonMode}
        onClick={() => (jsonMode ? setJsonMode(false) : enterJsonMode())}
      >
        <Code2 size={14} />
        {jsonMode ? 'Visual editor' : 'Advanced (JSON)'}
      </JsonToggle>

      {jsonMode ? (
        <>
          <JsonArea
            aria-label="Field rules JSON"
            spellCheck={false}
            $invalid={!!jsonError}
            value={jsonDraft}
            onChange={(e) => handleJsonChange(e.target.value)}
          />
          {jsonError
            ? <JsonError role="alert">{jsonError}</JsonError>
            : (
              <Hint>
                <Info size={14} />
                <span>Edits apply only when the JSON is valid. Switch back to the visual
                  editor anytime.</span>
              </Hint>
            )}
        </>
      ) : (
        <>
          {rules.length === 0 && (
            <Empty>
              No rules yet. Add one to show, hide, filter, or colour
              {currentField?.props?.label ? ` "${currentField.props.label}"` : ' this field'} based
              on other answers.
            </Empty>
          )}

          {rules.map((rule, index) => (
            <RuleEditorCard
              key={rule.id || index}
              rule={rule}
              index={index}
              siblingFields={siblingFields}
              cascadeFields={cascadeFields}
              formatSiblingFields={formatSiblingFields}
              availableEffects={availableEffects}
              onChange={(next) => updateRule(index, next)}
              onRemove={() => removeRule(index)}
            />
          ))}

          <AddButton type="button" onClick={addRule}>
            <Plus size={14} />
            Add Field Rule
          </AddButton>
        </>
      )}
    </Stack>
  );
};
